<template>
  <WdsStack gap-sm direction-column>
    <WdsSelect
      v-model="selectedCompanies"
      multi-select
      searchable
      :label="meta.filter.quickFilter ? '' : $t('Company')"
      :list="companyOptions"
      :placeholder="$t('All companies')"
      max-labels-visible="5"
      can-deselect
      labels-primary
      :loading="isLoading"
    />
  </WdsStack>
</template>

<script setup lang="ts">
import { WdsSelect, WdsStack } from '@worksome/wds-components'
import { computed, ModelRef } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/composables/translation'
import { graphql } from '@/gql'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'
import { useAccountsStore } from '@/components/GraphqlDataTable/filters/accounts/accounts-store'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

defineProps<{ meta: FilterMetaDataProp }>()

const selectedCompanies: ModelRef<{ label: string; value: string }[]> = defineModel({
  default: () => [],
})

const { actsAsSupplier } = useAccountCapabilities()

const { result, loading } = useQuery(
  graphql(`
    query RecruiterCompaniesFilter($id: ID!) {
      recruiter(id: $id) {
        id
        clients {
          id
          company {
            id
            name
          }
        }
      }
    }
  `),
  () => ({
    id: currentAccount.globalId,
  }),
  () => ({
    clientId: 'publicApi',
    enabled: !actsAsSupplier.value,
  }),
)

const { selectedAccounts } = useAccountsStore()

const { result: supplierResult, loading: supplierLoading } = useQuery(
  graphql(`
    query SupplierClientCompaniesFilter($accounts: [ID!]) {
      companyClients(accounts: $accounts, first: 100) {
        data {
          id
          company {
            id
            name
          }
        }
      }
    }
  `),
  () => ({
    ...(selectedAccounts.length === 0
      ? {}
      : { accounts: selectedAccounts.map((account) => account.id) }),
  }),
  () => ({
    clientId: 'publicApi',
    enabled: actsAsSupplier.value,
  }),
)

const isLoading = computed(() => (actsAsSupplier.value ? supplierLoading.value : loading.value))

const companyOptions = computed(() => {
  if (actsAsSupplier.value) {
    return (
      supplierResult.value?.companyClients.data.map(({ company }) => ({
        label: company.name,
        value: company.id,
      })) ?? []
    )
  }

  const seen = new Set<string>()

  return (
    result.value?.recruiter?.clients
      ?.filter(({ company }) => {
        if (seen.has(company.id)) return false
        seen.add(company.id)
        return true
      })
      .map(({ company }) => ({
        label: company.name,
        value: company.id,
      })) ?? []
  )
})
</script>
